import {
  CreateButton,
  Datagrid,
  EditButton,
  FunctionField,
  List,
  SimpleList,
  TextField,
  TextInput,
  TopToolbar,
  WrapperField,
  usePermissions,
} from "react-admin";
import type { RaRecord } from "react-admin";
import type { SxProps } from "@mui/material";
import { Box, useMediaQuery } from "@mui/material";

import { formatDisplayDateTime } from "@/utils/date-format";
import { hasPermission } from "@/utils/permission-utils";

import PhoneLink from "@/components/PhoneLink";
import {
  RelayAccessRequestMobileListPrimary,
  RelayAccessRequestMobileListSecondary,
} from "./RelayAccessRequestMobileListRow";
import RelayItsSourceChip from "./RelayItsSourceChip";
import { SendRelayApprovalInviteAction } from "./SendRelayApprovalInviteButton";
import {
  getAccessRequestDisplayName,
  getAccessRequestItsSource,
  getAccessRequestMobile,
} from "./utils";

const listRowSx = (): SxProps => ({
  borderBottom: 1,
  borderBottomColor: "divider",
  py: 1.5,
  px: 0.5,
});

const formatAccessRequestEvent = (record: RaRecord) => {
  const event = record.relayEvent as { name?: string } | undefined;
  return event?.name ?? "—";
};

const formatAccessRequestName = (record: RaRecord) => getAccessRequestDisplayName(record);

const listFilters = [<TextInput key="q" source="q" label="ITS ID" alwaysOn resettable />];

const ListActions = () => (
  <TopToolbar>
    <CreateButton />
  </TopToolbar>
);

const RelayAccessRequestsList = () => {
  const { permissions } = usePermissions();
  const canEdit =
    hasPermission(permissions, "relayAccessRequests.edit") ||
    hasPermission(permissions, "relayAccessRequests.*");
  const isNarrow = useMediaQuery((theme) => theme.breakpoints.down("md"), { noSsr: true });

  return (
    <List
      sort={{ field: "createdAt", order: "DESC" }}
      actions={<ListActions />}
      filters={listFilters}
    >
      {isNarrow ? (
        <SimpleList
          rowClick="show"
          primaryText={(record: RaRecord) => (
            <RelayAccessRequestMobileListPrimary record={record} />
          )}
          secondaryText={(record: RaRecord) => (
            <RelayAccessRequestMobileListSecondary record={record} canEdit={canEdit} />
          )}
          rowSx={() => listRowSx()}
        />
      ) : (
        <Box sx={{ width: "100%", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <Datagrid rowClick="show" bulkActionButtons={false} sx={{ minWidth: 1280 }}>
            <FunctionField
              label="Invite"
              render={(record: RaRecord) => (
                <Box onClick={(event) => event.stopPropagation()}>
                  <SendRelayApprovalInviteAction record={record} />
                </Box>
              )}
            />
            {canEdit ? (
              <WrapperField label="Edit">
                <EditButton />
              </WrapperField>
            ) : null}
            <TextField source="itsId" label="ITS ID" />
            <FunctionField
              label="Directory"
              render={(record: RaRecord) => (
                <RelayItsSourceChip source={getAccessRequestItsSource(record)} />
              )}
            />
            <FunctionField label="Name" render={formatAccessRequestName} />
            <FunctionField
              label="WhatsApp / mobile"
              render={(record: RaRecord) => (
                <PhoneLink phone={getAccessRequestMobile(record)} stopPropagation />
              )}
            />
            <FunctionField label="Event" render={formatAccessRequestEvent} />
            <TextField source="reason" emptyText="—" />
            <TextField source="remarks" emptyText="—" />
            <FunctionField
              label="Granted"
              sortBy="createdAt"
              render={(record: RaRecord) =>
                formatDisplayDateTime(record?.createdAt, { empty: "—" })
              }
            />
          </Datagrid>
        </Box>
      )}
    </List>
  );
};

export default RelayAccessRequestsList;
